import { adminAuthApi } from "@api/auth"
import { adminAuthStorage } from "@auth/storage"
import { logger } from "@lib/logger"
import { useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { createContext, useCallback, useContext, useState, type ReactNode } from "react"

import { ROLE } from "@/constants/roles"
import { env } from "@/env"
import type { AdminUser } from "@/types"

interface AuthContextValue {
  admin: AdminUser | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Synthesize a stable admin from the typed email — OFFLINE-DEV LOGIN FALLBACK
// only (VITE_ADMIN_MOCK), so a developer without a backend can get in.
function fallbackAdminFor(email: string): AdminUser {
  const local = email.split("@")[0] ?? "admin"
  const name = local
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
  return {
    id: "adm-dev-001",
    email,
    full_name: name || "Admin",
    role: ROLE.platformSuperuser,
    is_superadmin: true,
    institution: { id: "institution-dev", name: "Dev Institution" },
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(() => adminAuthStorage.getAdmin())
  const queryClient = useQueryClient()

  const login = useCallback(async (email: string, password: string) => {
    const trimmedEmail = email.trim()

    // ── OFFLINE-DEV LOGIN FALLBACK (VITE_ADMIN_MOCK) ─────────────────────
    // Only the LOGIN is faked here; the dashboard data source is the in-app
    // "Datos reales" / "Demo" toggle (defaults to real), and the management
    // console is always real.
    if (env.VITE_ADMIN_MOCK) {
      if (!trimmedEmail || !password.trim()) {
        throw new Error("invalid_credentials")
      }
      const devAdmin = fallbackAdminFor(trimmedEmail)
      adminAuthStorage.setMockSession()
      adminAuthStorage.setAdmin(devAdmin)
      setAdmin(devAdmin)
      logger.info("auth.admin_login_offline_dev", { institution: devAdmin.institution.name })
      return
    }

    // ── REAL login ───────────────────────────────────────────────────────
    try {
      const data = await adminAuthApi.login(trimmedEmail, password)
      adminAuthStorage.setTokens(data.access, data.refresh)
      adminAuthStorage.setAdmin(data.admin)
      setAdmin(data.admin)
      logger.info("auth.admin_login", { role: data.admin.role })
    } catch (err) {
      const code = isAxiosError(err) ? err.response?.data?.detail : null
      logger.warning("auth.admin_login_failed", {
        code,
        status: isAxiosError(err) ? err.response?.status : null,
      })
      // No response at all (offline, DNS, a dead backend) is not a wrong
      // password: a director who is told "incorrectos" resets a password that
      // was right. The login page maps each code through `errors:`.
      const noResponse = isAxiosError(err) && !err.response
      throw new Error(
        typeof code === "string" ? code : noResponse ? "network_error" : "invalid_credentials",
        { cause: err },
      )
    }
  }, [])

  const logout = useCallback(() => {
    adminAuthStorage.clear()
    setAdmin(null)
    // Drop every cached query so a different admin never sees the previous
    // org's data flash before refetch.
    queryClient.clear()
    logger.info("auth.admin_logout")
  }, [queryClient])

  return (
    <AuthContext.Provider value={{ admin, isAuthenticated: !!admin, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAdminAuth must be used within <AdminAuthProvider>")
  return ctx
}
